import { Layout, message } from 'antd';
import React, { Suspense, useEffect, useState } from 'react';

import { useDispatch, useSelector, useStore } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { formByIdSelector } from '../../store/selectors/formSelector';

import Loader from '../../components/Loader/Loader';
import RootLayout, { FormLayout } from '../../layout/Layout';

import { publishForm } from '../../store/actions/formActions';

import './style.less';

import ShareForm from '../../components/ShareForm';

import ActionHeader from '../../components/ActionHeader';
import { useMediaQuery } from 'react-responsive';
import { useTranslation } from 'react-i18next';

import Response from '../Response';
import Settings from '../../components/FormSettings';
import Build from '../../components/FormBuilder';

const Form = () => {
  const history = useHistory();
  const params = useParams();

  const { section } = params;

  const { t } = useTranslation(['builder']);

  const store = useStore();
  const dispatch = useDispatch();

  const { isLoading: isFormsLoading, initialLoad } = useSelector((state) => state.form);
  const { me } = useSelector((state) => state.auth);
  const form = formByIdSelector(store.getState(), params.id);

  const [selectedQuestionId, setSelectedQuestionId] = useState();

  const [previewMode, setPreviewMode] = useState(false);

  const [unpublishedChanges, setUnpublishedChanges] = useState(null);

  const [isPublishLoading, setPublishLoading] = useState(false);

  const isDesktop = useMediaQuery({ minWidth: 992 });
  const isMobile = useMediaQuery({ maxWidth: 767 });

  const [openQuestionPanel, setOpenQuestionPanel] = useState(isDesktop);
  const [openPropertiesPanel, setOpenPropertiesPanel] = useState(false);
  const [openLogicsPanel, setOpenLogicsPanel] = useState(false);

  useEffect(() => {
    if (!initialLoad && !isFormsLoading && !form) {
      history.push('/notfound');
    }
  }, [form, initialLoad, isFormsLoading, history]);

  const showPreview = () => {
    setOpenPropertiesPanel(false);
    setOpenQuestionPanel(false);
    if (isMobile) setSelectedQuestionId(null);
    setPreviewMode(true);
  };

  const hidePreview = () => {
    setPreviewMode(false);
    if (isDesktop && selectedQuestionId) setOpenPropertiesPanel(true);
  };

  const isLoading = initialLoad && isFormsLoading;

  // Use 'form.toBePublished' on initial mount and 'unpublishedChanges' after the first change.
  const isPublished =
    unpublishedChanges === null ? form?.toBePublished === false : unpublishedChanges === false;

  function renderTab() {
    switch (section) {
      case 'settings':
        return <Settings />;
      case 'responses':
        return <Response />;
      case 'share':
        return <ShareForm />;
      default:
        return (
          <Build
            previewMode={previewMode}
            selectedQuestionId={selectedQuestionId}
            setSelectedQuestionId={setSelectedQuestionId}
            setUnpublishedChanges={setUnpublishedChanges}
            openQuestionPanel={openQuestionPanel}
            setOpenQuestionPanel={setOpenQuestionPanel}
            openPropertiesPanel={openPropertiesPanel}
            setOpenPropertiesPanel={setOpenPropertiesPanel}
            openLogicsPanel={openLogicsPanel}
            setOpenLogicsPanel={setOpenLogicsPanel}
          />
        );
    }
  }

  const { duplicateFields } = useSelector((state) => state.builderErrors);

  const publishFormQuestions = async () => {
    if (duplicateFields.length) {
      const errorMessage = t(
        'errors.duplicateValues',
        'Duplicate values are not supported in option questions',
      );
      message.error(errorMessage);
      const errorEl = document.getElementById('duplicate-error');

      if (errorEl) {
        errorEl.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
      return;
    }
    setPublishLoading(true);
    dispatch(publishForm(form.id)).finally(() => setPublishLoading(false));
  };

  return !isLoading && form && me ? (
    <div className="Build">
      <FormLayout activeForm={form}>
        <Layout>
          <ActionHeader
            activeForm={form}
            hidePreview={hidePreview}
            showPreview={showPreview}
            previewMode={previewMode}
            publishFormQuestions={publishFormQuestions}
            isPublished={isPublished}
            isPublishLoading={isPublishLoading}
          />
          {renderTab()}
        </Layout>
      </FormLayout>
    </div>
  ) : (
    <Loader />
  );
};

export default function WrappedForm() {
  return (
    <Suspense fallback={<Loader />}>
      <Form />
    </Suspense>
  );
}
